import { COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS } from '@core/components/component-factory/component-factory-config-input.registry';
import type { ConfigComponentContract } from '@shared/utils/content-builder/config-component-contract';
import { computedResolvedConfig } from '@shared/utils/content-builder/block-config-computed-resolver';
import type { ShopCardWithLogoBlockConfig } from '@front-platform/content-builder';
import { shopCardWithLogoBlockResolveConfig } from '@front-platform/content-builder';
import { takeUntil, tap } from 'rxjs/operators';
import { combineLatest, Observable, of, Subject } from 'rxjs';
import { Component, OnDestroy, OnInit, input } from '@angular/core';

import { SettingsParametersState } from '@store/settings';
import { EntityStoreSelector } from '@common/selectors/entity.selector';

import { MarketplaceStoreSelector } from '@common/selectors/marketplace.selector';

import { DoodEntityModel, DoodEntityStatus } from '@core/models/entity.model';
import { IContentBuilderFieldImage } from '@core/models/content-builder-fields.model';

import { EntitiesService } from '@core/services/entities/entities.service';
import { EntityStatusService } from '@core/services/entity-status/entity-status.service';
import { ShopSearchParametersService } from '@core/services/shop-search-parameters/shop-search-parameters.service';
import { EntityCategoryBadgeSource } from '@shared/utils/entity-category-badge/entity-category-badge.utils';
import { resolveEntityDisplayName } from '@shared/utils/entity-display-name/entity-display-name.utils';
import { EntityKeys } from '@config/keys/entity.keys';

export interface ShopCardWithLogoBlockRuntimeConfig {
  'display_opened_shop'?: boolean;
  'display_closed_shop'?: boolean;
  'entity_category_badge_source'?: EntityCategoryBadgeSource;
  'kiosk_card_aspect_ratio'?: string;
  'entity_image_type'?: 'cover' | 'overview';
}

export type ShopCardWithLogoBlockConfigInput = Partial<
  Omit<ShopCardWithLogoBlockConfig, keyof ShopCardWithLogoBlockRuntimeConfig> &
    ShopCardWithLogoBlockRuntimeConfig
>;
export type ShopCardWithLogoBlockResolvedConfig = Omit<
  ShopCardWithLogoBlockConfig,
  keyof ShopCardWithLogoBlockRuntimeConfig
> &
  Partial<ShopCardWithLogoBlockRuntimeConfig>;
export interface ShopCardWithLogoBlockContract extends ConfigComponentContract<
  ShopCardWithLogoBlockConfigInput,
  ShopCardWithLogoBlockResolvedConfig
> {}

@Component({
  selector: 'app-shop-card-with-logo-block',
  templateUrl: './shop-card-with-logo-block.component.html',
  standalone: false,
})
export class ShopCardWithLogoBlockComponent
  implements OnInit, OnDestroy, ShopCardWithLogoBlockContract
{
  readonly config = input<ShopCardWithLogoBlockConfigInput>({});
  readonly resolvedConfig = computedResolvedConfig<
    ShopCardWithLogoBlockConfig,
    ShopCardWithLogoBlockRuntimeConfig
  >(shopCardWithLogoBlockResolveConfig, () => ({ config: this.config() }));
  get displayOpenedShop(): boolean {
    const value = this.resolvedConfig()['display_opened_shop'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get displayClosedShop(): boolean {
    const value = this.resolvedConfig()['display_closed_shop'];
    return value === undefined ? true : (value as unknown as boolean);
  }
  get shop(): any {
    return this.resolvedConfig()['shop'] as unknown as any;
  }
  get displayEntityImage(): boolean {
    return this.resolvedConfig()['display_entity_image'] as unknown as boolean;
  }
  get entityName(): string {
    return resolveEntityDisplayName({
      override: this.resolvedConfig()['entity_name'],
      entityName: this.entity?.[EntityKeys.Name],
      fallbackName: this.shop?.[0]?.[EntityKeys.Name],
    });
  }
  get entityCategory(): string {
    return this.resolvedConfig()['entity_category'] as unknown as string;
  }
  get image(): IContentBuilderFieldImage | undefined {
    return this.resolvedConfig()['image'] as unknown as IContentBuilderFieldImage | undefined;
  }
  get displayEntityCategory(): boolean {
    return this.resolvedConfig()['display_entity_category'] as unknown as boolean;
  }
  get entityCategoryBadgeSource(): EntityCategoryBadgeSource {
    return (
      (this.resolvedConfig()['entity_category_badge_source'] as EntityCategoryBadgeSource) ??
      'subcategory'
    );
  }
  get kioskCardAspectRatio(): string {
    return (this.resolvedConfig()['kiosk_card_aspect_ratio'] as unknown as string) || '16/9';
  }
  private _entityImageTypeConfigOverride: 'cover' | 'overview' | undefined;
  get entityImageType(): 'cover' | 'overview' {
    return (
      this._entityImageTypeConfigOverride ??
      (this.resolvedConfig()['entity_image_type'] as unknown as 'cover' | 'overview')
    );
  }
  set entityImageType(value: 'cover' | 'overview') {
    this._entityImageTypeConfigOverride = value;
  }

  isMarketplaceAvailable$ = this.marketplaceSelector.selectMarketplaceAvailable;
  isMarketplaceAvailable = false;

  private readonly destroyed$ = new Subject<boolean>();

  status?: DoodEntityStatus;
  entity!: DoodEntityModel;
  shopImage?: string;

  constructor(
    private entitySelector: EntityStoreSelector,
    private readonly entitiesService: EntitiesService,
    private marketplaceSelector: MarketplaceStoreSelector,
    private readonly entityStatusService: EntityStatusService,
    private readonly shopSearchParametersService: ShopSearchParametersService,
  ) {}

  componentFactoryIsBlockVisible$(): Observable<boolean> {
    return of(this.displayOpenedShop);
  }

  ngOnInit(): void {
    if (!this.shop?.[0]?.id) {
      return;
    }

    combineLatest([
      this.entitySelector.selectEntity(this.shop[0].id),
      this.shopSearchParametersService.getParameters$(),
    ])
      .pipe(
        takeUntil(this.destroyed$),
        tap(([entity, parameters]) => this.loadEntity(entity, parameters)),
      )
      .subscribe();

    this.isMarketplaceAvailable$
      .pipe(
        takeUntil(this.destroyed$),
        tap(isAvailable => (this.isMarketplaceAvailable = isAvailable)),
      )
      .subscribe();
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

  getShopUrl(entity: DoodEntityModel): string {
    return this.entitiesService.getUrl(entity);
  }

  private loadEntity(entity?: DoodEntityModel, parameters?: SettingsParametersState): void {
    let wantedAt = null;
    if (parameters?.wanted_at) {
      wantedAt = parameters?.wanted_at;
    }
    if (!wantedAt) {
      wantedAt = new Date();
    }

    this.status = this.entityStatusService.calculateStatus(
      entity,
      parameters?.distribution_mode,
      wantedAt,
    );
    this.entity = entity as DoodEntityModel;
    const images = entity?.images;
    if (this.entityImageType === 'overview') {
      this.shopImage = images?.overview?.url ?? images?.cover?.url;
    } else {
      this.shopImage = images?.cover?.url;
    }
    if (!this.shopImage && entity?.cover) {
      this.shopImage = 'https://api.dood.com' + entity?.cover;
    }
  }
}
COMPONENT_FACTORY_CONFIG_INPUT_COMPONENTS.set(ShopCardWithLogoBlockComponent, { 'keyAliases': {} });
